<template>
  <div class="modern-landing">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <h1 class="hero-title">Transforming Genomic Data into Clinical Insights</h1>
        <p class="hero-subtitle">
          We develop innovative solutions for variant interpretation in molecular tumor boards and biomedical research.
          Our work bridges the gap between complex genomic data and actionable clinical insights through
          machine learning, semantic web technologies,
          ontologies, and collaborative research.
        </p>
      </div>
    </section>

    <!-- Product Sections -->
    <section v-for="(product, index) in products" :key="index"
             :class="['product-section', index % 2 === 0 ? 'reverse' : '']">
      <div class="product-image-container">
        <a :href="product.link" class="product-link">
          <div class="image-glow"></div>
          <img :src="product.image" :alt="product.name + ' Logo'" class="product-image">
        </a>
      </div>

      <div class="product-content">
        <h2 class="product-title">
          <a :href="product.link" class="title-link">{{ product.name }}</a>
        </h2>
        <p class="product-description" v-html="product.description"></p>
        <a :href="product.link" class="cta-button">
          <span style="color:#000;">Go to {{ product.name }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </a>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      products: [
        {
          name: "Onkopus",
          link: "https://mtb.bioinf.med.uni-goettingen.de/onkopus/",
          image: require("@/assets/images/onkopus_logo_v0.1.4.1_640.png"),
          description: "Modular variant interpretation framework for molecular tumor boards (MTBs) and biomedical research. Users can search for variants and upload variant files and receive a comprehensive overview of pathogenicity predictions, functional consequences and potential molecular treatments."
        },
        {
          name: "SeqCAT",
          link: "https://mtb.bioinf.med.uni-goettingen.de/SeqCAT/",
          image: require("@/assets/images/seqcat.png"),
          description: "The Coordinate Converting Service (CCS) is responsible for providing information in genome, protein and transcript data. You have access to sequence data and basic functions to obtain information on transcripts and genes or to switch between reference genomes."
        },
        {
          name: "AdaGenes",
          link: "https://mtb.bioinf.med.uni-goettingen.de/adagenes/",
          image: require("@/assets/images/adagenes_v450x650.png"),
          description: "A high-throughput VCF streaming processor for annotating and filtering whole genome sequencing and whole exome sequencing data. AdaGenes integrates genomic, transcript, and protein data annotations, while maintaining scalability and performance as well as an intuitive user interface for defining filtering options."
        },
        {
          name: "KNIME Workflows",
          link: "https://hub.knime.com/bioinf_goe/spaces/Public/~Zd6-FraRjnV7MMpu/",
          image: require("@/assets/images/KNIMELogoTM.svg.png"),
          description: "The KNIME workflows are a set of automated workflows for precision medicine, multi-omics data analysis, and drug screen analysis. These workflows can be utilized for a multitude of bioinformatic workflows, such as the implementation of customized workflows for annotating, filtering, and interpreting genetic variants, or comparing drug screens with clinical outcomes."
        },
        {
          name: "DrugOn",
          link: "https://mtb.bioinf.med.uni-goettingen.de/drugon-web/",
          image: require("@/assets/images/drugon.png"),
          description: "DrugOn is a drug ontology that contains information from multiple public data sources such as DrugBank, ATC codes or MeSH terms. Using a selection process, we can identify the best options in terms of drug classes and categories, molecular targets, and outlinks and other annotations based on these sources."
        },
        {
          name: "PanCNV-Explorer",
          link: "https://mtb.bioinf.med.uni-goettingen.de/pancnv-explorer/",
          image: require("@/assets/images/CNV-Database_Logo.png"),
          description: "The PanCNV-Explorer shows information about copy number variation in different cancer entities."
        },
        {
          name: "MTB-Report",
          link: "https://mtb.bioinf.med.uni-goettingen.de/mtb-report/",
          image: require("@/assets/images/mtbreport.png"),
          description: "MTB-Report in an interactive web application to analyze actionable variants. Supports molecular tumor boards (MTBs) or scientific data projects. <br/><br /><b>Publication:</b><br />Kurz, N. S., Perera Bel, J., Höltermann, C., Tucholski, T., Yang, J., Beissbarth, T., & Dönitz, J. (2022). Identifying actionable variants in cancer-The dual web and batch processing tool MTB-report. Stud Health Technol Inform. 2022 Aug 17; 296: 73-80.<br />"
        }
      ]
    }
  }
}
</script>

<style scoped>
:root {
  --primary: #2563eb;
  --primary-dark: #1d4ed8;
  --secondary: #7c3aed;
  --light: #f8fafc;
  --dark: #0f172a;
  --gray: #64748b;
  --light-gray: #e2e8f0;
  --transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.modern-landing {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
  color: var(--dark);
  background-color: #fff;
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  padding: 5rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%);
  z-index: 0;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: var(--dark);
  letter-spacing: -0.025em;
  /* Fallback for browsers that don't support background-clip: text */
  -webkit-background-clip: text;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: var(--gray);
  max-width: 700px;
  margin: 0 auto;
}

/* Product Sections */
.product-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  padding: 6rem 5%;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.product-section:nth-child(even) {
  background-color: var(--light);
}

.product-section.reverse {
  flex-direction: row-reverse;
}

.product-section.reverse .product-image-container {
  order: 2;
}

.product-section.reverse .product-content {
  order: 1;
}

/* Image Container */
.product-image-container {
  position: relative;
  display: flex;
  justify-content: center;
  transition: var(--transition);
  z-index: 1;
}

.image-glow {
  position: absolute;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(20px);
  z-index: -1;
  animation: pulse 8s infinite;
}

.product-image {
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  transition: var(--transition);
  object-fit: contain;
  border: 1px solid var(--light-gray);
  background: white;
  padding: 1.5rem;
}

.product-image-container:hover .product-image {
  transform: translateY(-10px);
  box-shadow: 0 35px 60px -15px rgba(0, 0, 0, 0.2);
}

/* Product Content */
.product-content {
  padding: 1rem;
}

.product-title {
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.title-link {
  color: var(--dark);
  text-decoration: none;
  position: relative;
}

.title-link::after {
  content: "";
  position: absolute;
  width: 0;
  height: 3px;
  bottom: 4px;
  left: 0;
  background: var(--primary);
  transition: var(--transition);
}

.title-link:hover::after {
  width: 100%;
}

.product-description {
  font-size: 1.1rem;
  color: var(--gray);
  margin-bottom: 2rem;
  line-height: 1.7;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 2rem;
  background: linear-gradient(to right, var(--primary), var(--secondary));
  color: white !important;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: var(--transition);
  box-shadow: 0 10px 25px -5px rgba(37, 99, 235, 0.3);
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.cta-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, var(--primary-dark), #5850ec);
  opacity: 0;
  transition: var(--transition);
  z-index: -1;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 30px -5px rgba(37, 99, 235, 0.4);
}

.cta-button:hover::before {
  opacity: 1;
}

.cta-button svg {
  transition: var(--transition);
  stroke: white;
}

.cta-button:hover svg {
  transform: translateX(4px);
}

/* Animations */
@keyframes pulse {
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 0.4; }
  100% { transform: scale(1); opacity: 0.7; }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .product-section {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 4rem 5%;
  }

  .product-image-container {
    max-width: 400px;
    margin: 0 auto;
  }

  .product-content {
    max-width: 600px;
    margin: 0 auto;
  }

  .product-description {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .product-title {
    font-size: 1.8rem;
  }

  .product-section {
    padding: 3rem 5%;
  }

  .image-glow {
    width: 250px;
    height: 250px;
  }
}

@media (max-width: 480px) {
  .hero {
    padding: 3rem 1.5rem;
  }

  .hero-title {
    font-size: 2rem;
  }

  .product-section {
    padding: 2.5rem 4%;
  }

  .product-image {
    max-width: 280px;
    padding: 1rem;
  }

  .cta-button {
    width: 100%;
    justify-content: center;
  }
}
</style>